import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Avatar, AvatarFallback } from "../components/ui/Avatar";
import { Search, Plus, Shield, Mail, Edit, Trash2, UserCheck } from "lucide-react";
import { adminsService, Admin } from "../services/admins";
import { toast } from "sonner";

interface AdministradoresPageProps {
  onNavigate?: (page: string) => void;
}

export function AdministradoresPage({ onNavigate }: AdministradoresPageProps = {}) {
  const navigate = useNavigate();
  const { user: currentUser } = useOutletContext<any>() || { user: null };
  
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    active_total: 0,
    inactive_total: 0
  });

  const fetchAdmins = async (page = 1) => {
    try {
      setLoading(true);
      // Busca os admins paginando por 20 (padrão do backend agora)
      const response = await adminsService.getAll({ page, per_page: 20 });
      const responseData = response.data.data || response.data;
      
      setAdmins(responseData.data || []);
      
      const meta = responseData.meta;
      if (meta) {
        setPagination({
          current_page: meta.current_page,
          last_page: meta.last_page,
          total: meta.total,
          active_total: meta.active_total || 0,
          inactive_total: meta.inactive_total || 0
        });
      }
    } catch (error) {
      console.error("Erro ao buscar administradores:", error);
      toast.error("Erro ao carregar os administradores.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins(1);
  }, []);

  const handleDeactivate = async (admin: Admin) => {
    if (currentUser && admin.id === currentUser.id) {
      toast.error("Não é possível desativar a própria conta.");
      return;
    }

    if (window.confirm(`Tem certeza que deseja desativar o administrador "${admin.name}"?`)) {
      try {
        await adminsService.deactivate(admin.id);
        toast.success("Administrador desativado com sucesso!");
        fetchAdmins(pagination.current_page);
      } catch (error) {
        console.error("Erro ao desativar administrador:", error);
        toast.error("Erro ao desativar o administrador.");
      }
    }
  };

  const handleActivate = async (admin: Admin) => {
    try {
      await adminsService.activate(admin.id);
      toast.success("Administrador ativado com sucesso!");
      fetchAdmins(pagination.current_page);
    } catch (error) {
      console.error("Erro ao ativar administrador:", error);
      toast.error("Erro ao ativar o administrador.");
    }
  };

  const handleEditClick = (admin: Admin) => {
    if (onNavigate) {
      onNavigate(`/editar-administrador/${admin.id}`);
    } else {
      navigate(`/editar-administrador/${admin.id}`);
    }
  };

  const handleNewClick = () => {
    if (onNavigate) {
      onNavigate("novo-administrador");
    } else {
      navigate("/novo-administrador");
    }
  };

  // Como a busca local atua sobre a página atual (normalmente todos os admins cabem em 1 página de 20)
  const filteredAdmins = admins.filter((admin) => {
    const term = searchTerm.toLowerCase();
    return (
      admin.name.toLowerCase().includes(term) ||
      admin.email.toLowerCase().includes(term)
    );
  });



  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1>Administradores</h1>
        <p className="text-gray-600">Gerencie os usuários administradores do sistema</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total de Admins</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
            )}
            <p className="text-xs text-gray-600 mt-1">Usuários cadastrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-green-600">{pagination.active_total}</p>
            )}
            <p className="text-xs text-gray-600 mt-1">Com acesso ao painel</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Inativos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-7 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-red-600">{pagination.inactive_total}</p>
            )}
            <p className="text-xs text-gray-600 mt-1">Acesso suspenso</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar administradores..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={handleNewClick}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Administrador
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : filteredAdmins.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          Nenhum administrador encontrado.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
            {filteredAdmins.map((admin) => {
              const initials = admin.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .substring(0, 2)
                .toUpperCase();

              const isSelf = currentUser && admin.id === currentUser.id;

              return (
                <Card key={admin.id} className="hover:shadow-md transition-shadow p-4 flex flex-col justify-between h-full">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar className="w-10 h-10 shrink-0">
                          <AvatarFallback className="bg-gray-200 text-gray-700 text-sm font-semibold">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 truncate" title={admin.name}>
                            <span className="truncate">{admin.name}</span>
                            {isSelf && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#D4C070] text-[#D4C070] font-normal shrink-0">
                                Você
                              </span>
                            )}
                          </h3>
                          <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                            <Shield className="h-3 w-3 text-blue-500" />
                            Admin
                          </p>
                        </div>
                      </div>
                      <Badge variant={admin.is_active ? "default" : "destructive"} className="text-[10px] px-1.5 py-0 shrink-0">
                        {admin.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0 pt-2 border-t">
                      <Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="truncate" title={admin.email}>{admin.email}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 mt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs h-8"
                      onClick={() => handleEditClick(admin)}
                    >
                      <Edit className="h-3 w-3 mr-1.5" />
                      Editar
                    </Button>

                    {admin.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2.5"
                        onClick={() => handleDeactivate(admin)}
                        disabled={isSelf}
                        title={isSelf ? "Não é possível desativar a própria conta" : "Desativar"}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 h-8 px-2.5"
                        onClick={() => handleActivate(admin)}
                        title="Ativar"
                      >
                        <UserCheck className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Paginação */}
          {pagination.last_page > 1 && (
            <div className="flex items-center justify-center mt-6 mb-6">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page === 1}
                  onClick={() => fetchAdmins(pagination.current_page - 1)}
                >
                  {'<'}
                </Button>
                {(() => {
                  const current = pagination.current_page;
                  const last = pagination.last_page;
                  const pages: (number | string)[] = [];
                  const delta = 2;

                  for (let i = 1; i <= last; i++) {
                    if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
                      pages.push(i);
                    } else if (pages[pages.length - 1] !== "...") {
                      pages.push("...");
                    }
                  }

                  return pages.map((page, index) => (
                    page === "..." ? (
                      <span key={`dots-${index}`} className="text-gray-400 px-1">...</span>
                    ) : (
                      <Button
                        key={page}
                        variant={pagination.current_page === page ? "default" : "outline"}
                        size="sm"
                        className={pagination.current_page === page ? "bg-[#D4C070] hover:bg-[#D4C070] text-white" : "text-[#68686A]"}
                        onClick={() => fetchAdmins(page as number)}
                      >
                        {page}
                      </Button>
                    )
                  ));
                })()}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page === pagination.last_page}
                  onClick={() => fetchAdmins(pagination.current_page + 1)}
                >
                  {'>'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}

export default AdministradoresPage;
